/**
 * Pure helpers for rewriting public service origins to their internal (private
 * network) counterparts. Kept free of `config`/environment access so they can
 * be unit-tested in isolation; the SSR/config glue lives in `utils/request.ts`.
 */

/** A raw `[publicUrl, internalUrl]` pair as read from config. Either side may be undefined/empty. */
export type InternalUrlMapping = [publicUrl: string | undefined, internalUrl: string | undefined];

/** A normalised pair: both sides present and trailing-slash-trimmed. */
export type CleanInternalUrlMapping = [publicBase: string, internalBase: string];

const trimTrailingSlash = (value: string): string => (value.endsWith('/') ? value.slice(0, -1) : value);

/**
 * A path boundary is the end of the string or the start of a new
 * path/query/fragment segment — so a base like ".../api" doesn't also match
 * ".../api-something".
 */
const isPathBoundary = (char: string | undefined): boolean => char === undefined || char === '/' || char === '?' || char === '#';

/**
 * Normalise a raw mapping list: drop pairs missing either URL and trim trailing
 * slashes on both sides. Run this once (e.g. at module load) so the per-request
 * rewrite stays a tight loop over ready-to-use pairs.
 */
export function mapInternalUrls(mappings: InternalUrlMapping[]): CleanInternalUrlMapping[] {
  return mappings
    .filter((pair): pair is [string, string] => pair.every(Boolean))
    .map(([publicUrl, internalUrl]): CleanInternalUrlMapping => [trimTrailingSlash(publicUrl), trimTrailingSlash(internalUrl)]);
}

/**
 * Returns `url` with the first matching public base swapped for its internal
 * counterpart. Matching is anchored on a path boundary so a shared prefix
 * (".../api" vs ".../api-foo") can't cause a wrong rewrite. Expects a normalised
 * mapping from `mapInternalUrls`. When nothing matches, `url` is returned
 * unchanged.
 */
export function rewriteToInternalUrl(url: string, mappings: CleanInternalUrlMapping[]): string {
  for (const [publicBase, internalBase] of mappings) {
    if (url.startsWith(publicBase) && isPathBoundary(url[publicBase.length])) {
      return internalBase + url.slice(publicBase.length);
    }
  }

  return url;
}
